import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { FretboardView, type FretboardInstrument, type FretboardOptions } from './fretboard';
import type { VizNote } from './types';

export interface FretboardCanvasProps {
  instrument: FretboardInstrument;
  active: readonly VizNote[];
  context: readonly VizNote[];
  /** Touch input defaults to on when `onPick` is given. */
  options?: Partial<FretboardOptions>;
  /** Touch-input pick: string (1 = highest) and fret relative to the capo. */
  onPick?: (string: number, fret: number) => void;
  /** CSS px (default grows with the string count: 6 strings → 180). */
  height?: number;
  className?: string;
  style?: CSSProperties;
}

/** React wrapper around FretboardView; pushes prop changes into the canvas view (redraws only on change). */
export function FretboardCanvas({ instrument, active, context, options, onPick, height, className, style }: FretboardCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<FretboardView | null>(null);
  const pickRef = useRef(onPick);

  useLayoutEffect(() => {
    pickRef.current = onPick;
  });

  useLayoutEffect(() => {
    const view = new FretboardView(canvasRef.current!);
    viewRef.current = view;
    view.onPick((s, f) => pickRef.current?.(s, f));
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    viewRef.current?.setInstrument(instrument);
  }, [instrument]);

  const touch = options?.touchInput ?? !!onPick;
  useLayoutEffect(() => {
    viewRef.current?.setOptions({ ...options, touchInput: touch });
  }, [options, touch]);

  useLayoutEffect(() => {
    viewRef.current?.setNotes(active, context);
  }, [active, context]);

  const strings = instrument.strings ?? instrument.tuning.length;
  const h = height ?? Math.max(96, strings * 24 + 36);
  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label={`${instrument.name ?? 'Fretboard'} fretboard`}
      style={{ display: 'block', width: '100%', height: h, touchAction: 'manipulation', ...style }}
    />
  );
}
